import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PropTypes from 'prop-types';
import React, {memo, useState} from 'react';
import {Alert, Button, Grid, Paper, Stack, Typography} from '@mui/material';
import {UPLOAD_LABELS, UPLOAD_STATES} from '@app/constants/uploads.js';
import {useTranslation} from 'react-i18next';

import ReviewUploadDialog from './ReviewUploadDialog.jsx';

const UploadItem = ({upload}) => {
  const {t} = useTranslation();

  const [review, setReview] = useState(false);

  // count number of errors and alternates
  // the fallback is temporarily while the API is being updated
  // https://hello.planet.com/code/apps/granero/-/merge_requests/437
  const numErrors = upload.counts?.total_errors || upload.total_errors;
  const numAlternates =
    upload.counts?.total_alternates || upload.total_alternates;

  const requiresReview =
    upload.state === UPLOAD_STATES.REVIEW &&
    (numErrors > 0 || numAlternates > 0);

  /* displays information if this upload requires attention from the user */
  const InfoBox = () => {
    let messageColor = 'warning';
    let message;
    if (numErrors > 0) {
      messageColor = 'error';

      /* create a message displaying both errors and alternates. There's a test for each combination. */
      const alternateMessage = ` and ${numAlternates} feature${numAlternates > 1 ? 's' : ''} requiring simplification`;
      message = `${numErrors} error${numErrors > 1 ? 's' : ''}${
        numAlternates > 0 ? alternateMessage : ''
      }`;
    } else {
      /* in most cases, we just need to show how many alternates need to be accepted. */
      message = `Simplify ${numAlternates} feature${numAlternates > 1 ? 's' : ''}`;
    }

    return (
      <Alert
        severity={messageColor}
        sx={{
          px: 1,
          py: 0,
          mr: 1,
        }}
      >
        {message}
      </Alert>
    );
  };

  /* displays the most relevant timestamp (either completed at or uploaded at) */
  const Timestamp = () => {
    let message = 'Uploaded';
    let timestamp = upload.created_at;
    if (upload.completed_at) {
      message = 'Completed';
      timestamp = upload.completed_at;
    }
    return (
      <Typography variant="body2" color="textSecondary">
        {message}:{' '}
        <time
          dateTime={t('common:date_time.med', {
            date: timestamp,
          })}
        >
          {t('common:date_time.med', {
            date: timestamp,
          })}
        </time>
      </Typography>
    );
  };

  return (
    <>
      <Paper
        variant="outlined"
        elevation={0}
        sx={{
          padding: 1,
        }}
      >
        <Grid container>
          <Grid sm={6} md={8} lg={8} display="flex" alignItems="center" item>
            <Typography
              variant="subtitle1"
              textOverflow="ellipsis"
              overflow="hidden"
              noWrap
            >
              {upload.files.join(', ')}
            </Typography>
          </Grid>
          <Grid sm={6} md={4} lg={4} alignItems="center" item>
            <Typography align="right">{UPLOAD_LABELS[upload.state]}</Typography>
          </Grid>
        </Grid>
        <Timestamp />
        <Stack direction="row" display="flex" alignItems="center">
          {requiresReview && <InfoBox />}

          {[UPLOAD_STATES.INGESTED, UPLOAD_STATES.REVIEW].includes(
            upload.state
          ) && (
            <Button
              onClick={() => {
                setReview(true);
              }}
              color="primary"
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              sx={{
                height: theme => theme.spacing(3.75),
                minWidth: theme => theme.spacing(10),
                flexShrink: 0,
                ml: 'auto',
              }}
            >
              {requiresReview ? 'Review features' : 'Finish upload'}
            </Button>
          )}
        </Stack>
      </Paper>

      {review && (
        <ReviewUploadDialog
          upload={upload}
          open={review}
          onClose={() => {
            setReview(false);
          }}
        />
      )}
    </>
  );
};

UploadItem.propTypes = {
  upload: PropTypes.object,
};

export default memo(UploadItem);
